import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Calendar, MapPin, ArrowRight, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/lib/seo";

/**
 * Onboarding.jsx — Premium Dark Luxury onboarding for a brand-new client
 * who has just registered and has no MyEvent yet.
 *
 * Reuses:
 *   - Dark Luxury design tokens (card-luxe, font-serif-luxe, btn-gold, gold-text)
 *   - Existing /api/next/my-events POST endpoint
 *   - AuthContext.user (for the greeting)
 *
 * Silently reuses `user.type_evenement` from the registration form — no
 * extra field asked. Only 3 inputs: nom du couple/événement, date, lieu.
 */
export default function Onboarding() {
  useSeo({
    title: "Créez votre événement · MyEvent",
    description: "Commencez à préparer votre événement en un instant.",
    path: "/onboarding-myevent",
  });

  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", date: "", lieu: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) {
      setErr("Merci d'indiquer un nom pour votre événement.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/next/my-events", {
        name: form.name.trim(),
        date: form.date || null,
        lieu: form.lieu.trim() || null,
      });
      const eventId = data?.event?.id;
      if (!eventId) throw new Error("Événement non créé");
      nav(`/myevent/${eventId}`, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const suggestedName =
    user?.type_evenement === "Mariage" && user?.prenom
      ? `Mariage de ${user.prenom}`
      : "";

  return (
    <main className="min-h-screen bg-black flex items-center justify-center px-6 py-24 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -left-40 w-[680px] h-[680px] rounded-full bg-[#DCB457]/[0.07] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[680px] h-[680px] rounded-full bg-[#DCB457]/[0.05] blur-3xl" />

      <div className="relative w-full max-w-[540px]" data-testid="onboarding-page">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 border border-[#DCB457]/30 px-4 py-1.5 text-[10px] uppercase tracking-[0.32em] text-[#DCB457]/90">
            <Sparkles size={12} strokeWidth={1.4} />
            <span>Bienvenue{user?.prenom ? `, ${user.prenom}` : ""}</span>
          </div>
        </div>

        <div className="card-luxe p-8 md:p-11">
          <h1 className="font-serif-luxe text-3xl md:text-4xl leading-tight">
            Créons votre <span className="italic gold-text">événement.</span>
          </h1>
          <p className="text-white/60 text-sm mt-3 mb-9 leading-relaxed">
            Un instant pour poser les bases — vous pourrez tout affiner
            ensuite dans votre espace MyEvent.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="onboarding-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">
                Nom du couple ou de l'événement *
              </label>
              <div className="relative">
                <Sparkles size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70" strokeWidth={1.2} />
                <input
                  type="text"
                  required
                  data-testid="onboarding-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder={suggestedName || "Ex. Mariage de Camille & Louis"}
                  className="w-full bg-black/60 border border-[#DCB457]/25 pl-10 pr-3 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">
                  Date de l'événement
                </label>
                <div className="relative">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70" strokeWidth={1.2} />
                  <input
                    type="date"
                    data-testid="onboarding-date"
                    value={form.date}
                    onChange={set("date")}
                    className="w-full bg-black/60 border border-[#DCB457]/25 pl-10 pr-3 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">
                  Lieu (optionnel)
                </label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70" strokeWidth={1.2} />
                  <input
                    type="text"
                    data-testid="onboarding-lieu"
                    value={form.lieu}
                    onChange={set("lieu")}
                    placeholder="Château, salle, ville…"
                    className="w-full bg-black/60 border border-[#DCB457]/25 pl-10 pr-3 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            {err && (
              <div className="flex items-start gap-2 border border-red-400/40 bg-red-400/5 px-3 py-2 text-xs text-red-300" data-testid="onboarding-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="onboarding-submit"
              className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-3"
            >
              {loading ? "Création…" : "Créer mon événement"} <ArrowRight size={16} strokeWidth={1.4} />
            </button>

            <p className="text-center text-[10px] uppercase tracking-[0.24em] text-white/40 pt-2">
              Vous pourrez tout modifier ensuite
            </p>
          </form>
        </div>

        <p className="text-center text-[10px] uppercase tracking-[0.28em] text-white/40 mt-8">
          <button
            type="button"
            onClick={() => { try { localStorage.removeItem("ma_token"); } catch { /* ignore */ } window.location.href = "/connexion"; }}
            data-testid="onboarding-logout"
            className="hover:text-[#DCB457] transition"
          >
            Se déconnecter
          </button>
        </p>
      </div>
    </main>
  );
}
